'use client'

import { SignType } from '@/features/Auth/auth.types'
import type { AppWalletAdapter } from '@/shared/wallet/wallet.types'
import { useAppKitAccount, useAppKitProvider, useDisconnect } from '@reown/appkit/react'
import { useAppKitWallet } from '@reown/appkit-wallet-button/react'
import type { Provider as SolanaWalletConnectProvider } from '@reown/appkit-adapter-solana/react'
import { useCallback } from 'react'
import type { VersionedTransaction } from '@solana/web3.js'

export function useWalletConnectWallet(): AppWalletAdapter {
  const { address, isConnected } = useAppKitAccount({ namespace: 'solana' })
  const { walletProvider } = useAppKitProvider<SolanaWalletConnectProvider>('solana')
  const { connect: wcConnect, isPending: connecting } = useAppKitWallet({ namespace: 'solana' })
  const { disconnect: wcDisconnect } = useDisconnect()

  const connect = useCallback(async () => {
    try {
      await wcConnect('walletConnect')
    } catch (e) {
      // user closed the QR modal / rejected the session
    }
  }, [wcConnect])

  const disconnect = useCallback(async () => {
    await wcDisconnect()
  }, [wcDisconnect])

  const signMessage = useCallback(async (message: Uint8Array) => {
    if (!walletProvider) {
      throw new Error('WalletConnect: no active session to sign with')
    }
    return walletProvider.signMessage(message)
  }, [walletProvider])

  const signTransaction = useCallback(async (tx: VersionedTransaction) => {
    if (!walletProvider) {
      throw new Error('WalletConnect: no active session to sign with')
    }
    return walletProvider.signTransaction(tx)
  }, [walletProvider])

  return {
    connected: isConnected,
    connecting,
    publicKey: address ?? null,
    walletName: 'WalletConnect',
    confirmType: SignType.MESSAGE,
    connect,
    disconnect,
    signMessage,
    signTransaction,
  }
}
